/**
 * domQuery — the one narrowing path between `querySelector` and a typed element (#975).
 *
 * `ParentNode.querySelector()` returns `Element | null`, and `Element` carries none of
 * `.value`, `.checked`, `.style` or the `on*` handlers. Reading one of those off a raw
 * query result hides two different runtime failures behind a single type error:
 *
 *   1. A selector that stops matching returns `null`, and the read throws
 *      `TypeError: Cannot read properties of null (reading 'value')`. The message names
 *      the property and never the selector, so the site is found by bisection.
 *   2. A selector that matches the WRONG element returns an `Element` without the
 *      property. The read yields `undefined`, and a write lands on a property nobody
 *      reads. Nothing throws at all. That is the worse of the two, and it is what
 *      views/vectorSpaceScene.js had at 29 sites.
 *
 * `el()` closes both. It names the selector and the calling context in the thrown
 * message, and it asserts the element type before returning, so failure 2 becomes as
 * loud as failure 1. A blanket `as HTMLInputElement` at each site would have silenced
 * the checker and left both failures exactly where they were.
 */

/**
 * Thrown by `el()`. Carries the selector, the context and whatever did match, so a
 * caller can report more than the message string.
 */
export class SelectorError extends Error {
  /**
   * @param {string} message
   * @param {{ selector: string, context?: string, found?: Element | null }} detail
   */
  constructor(message, detail) {
    super(message);
    this.name = 'SelectorError';
    /** @type {string} */
    this.selector = detail.selector;
    /** @type {string | undefined} */
    this.context = detail.context;
    /** @type {Element | null} */
    this.found = detail.found || null;
    // Babel's ES5 class transform drops the subclass prototype when the base is a
    // built-in, which breaks `instanceof SelectorError`. Restoring it explicitly keeps
    // the check valid under every transform this repo builds and tests with.
    Object.setPrototypeOf(this, SelectorError.prototype);
  }
}

/**
 * Read a constructor's own name for the error message, without asking the type system
 * to describe a `Function` it only knows as a construct signature.
 *
 * @param {unknown} Ctor
 * @returns {string}
 */
function constructorName(Ctor) {
  if (typeof Ctor === 'function' && Ctor.name) return Ctor.name;
  return 'the expected element type';
}

/**
 * Query one element, assert its type, and return it narrowed.
 *
 * @template {Element} T
 * @param {ParentNode} root - Element, document or fragment to search inside.
 * @param {string} selector - CSS selector, passed straight to `querySelector`.
 * @param {new () => T} Ctor - Expected element constructor, e.g. `HTMLInputElement`.
 * @param {string} [context] - Where the query lives, e.g. `'vectorSpaceScene'`.
 * @returns {T}
 * @throws {SelectorError} when nothing matches, or when the match is another type.
 */
export function el(root, selector, Ctor, context) {
  const where = context ? ` in ${context}` : '';
  const found = root.querySelector(selector);
  if (found === null) {
    throw new SelectorError(
      `selector \`${selector}\` matched nothing${where}`,
      { selector, context, found: null },
    );
  }
  if (!(found instanceof Ctor)) {
    throw new SelectorError(
      `selector \`${selector}\` matched <${found.tagName.toLowerCase()}>${where}, `
        + `which is not ${constructorName(Ctor)}`,
      { selector, context, found },
    );
  }
  return found;
}
